import { Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import MainLayout from "./components/MainLayout";
import Home from "./pages/Home";
import Profitability from "./pages/Profitability";
import "antd/dist/reset.css";
import "./styles.css";
import Finance from "./pages/Finance";
import HR from "./pages/HR";
import Login from "./pages/Login";
import Supply from "./pages/Supply";
import Fournisseurs from "./pages/Fournisseurs";
import Resources from "./pages/Resources";
import Catalog from "./pages/Catalog";
import Distributeurs from "./pages/Distributeurs";
import ProjectsPage from "./pages/Projects/ProjectsPage.tsx";
import HarvenChat from "./pages/Harven/HarvenChat.tsx";
import { useAppSelector } from "./store";

// Composant d'index qui redirige automatiquement en fonction des accès
const RootIndex = () => {
  const { isAuthenticated, currentUser } = useAppSelector((s) => s.user);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const accesses = currentUser?.accesses || [];
  const isSupplierUser = Boolean(currentUser?.departmentFlags?.isSupplier);
  // Admin: afficher la Home par défaut
  if (accesses.includes("admin")) {
    return <Home />;
  }

  // Même logique que la Sidebar pour déterminer les modules visibles
  const available: Array<{ path: string; key: string }> = [];

  if (!isSupplierUser) {
    available.push({ path: "/ressources", key: "ressources" });
  }

  if (accesses.includes("analyse"))
    available.push({ path: "/rentabilite", key: "rentabilite" });
  if (accesses.includes("finance"))
    available.push({ path: "/finance", key: "finance" });
  if (accesses.includes("reporting"))
    available.push({ path: "/fournisseurs", key: "fournisseurs" });
  if (accesses.includes("approvisionnement"))
    available.push({ path: "/approvisionnement", key: "approvisionnement" });
  if (accesses.includes("hr")) available.push({ path: "/rh", key: "rh" });
  if (accesses.includes("catalog"))
    available.push({ path: "/catalogue", key: "catalogue" });
  if (accesses.includes("retailers"))
    available.push({ path: "/distributeurs", key: "distributeurs" });
  
  available.push({ path: "/projets", key: "projets" });
  available.push({ path: "/harven", key: "harven" });

  // Forcer la redirection des suppliers vers le reporting
  if (isSupplierUser && accesses.includes("reporting")) {
    return <Navigate to="/fournisseurs" replace />;
  }

  // Rediriger vers le premier module disponible (ex: reporting -> /fournisseurs)
  const target = available.length > 0 ? available[0].path : "/login";
  return <Navigate to={target} replace />;
};

function App() {
  return (
    <Routes>
      {/* Route de connexion séparée du layout principal */}
      <Route path="/login" element={<Login />} />

      {/* Routes protégées avec MainLayout */}
      <Route path="/" element={<MainLayout />}>
        {/* Redirection dynamique selon les accès (évite l'écran blanc sur '/') */}
        <Route index element={<RootIndex />} />

        {/* Les pages financières requièrent l'accès admin */}
        <Route
          path="rentabilite"
          element={
            <ProtectedRoute requiredAccess="admin">
              <Profitability />
            </ProtectedRoute>
          }
        />
        <Route
          path="finance"
          element={
            <ProtectedRoute requiredAccess="admin">
              <Finance />
            </ProtectedRoute>
          }
        />


        {/* Section Ressources Humaines - requiert l'accès RH ou admin */}
        <Route
          path="rh"
          element={
            <ProtectedRoute requiredAccess={["hr", "admin"]}>
              <HR />
            </ProtectedRoute>
          }
        />
        <Route
          path="ressources-humaines"
          element={
            <ProtectedRoute requiredAccess={["hr", "admin"]}>
              <HR />
            </ProtectedRoute>
          }
        />
        <Route
          path="approvisionnement"
          element={
            <ProtectedRoute requiredAccess={["approvisionnement", "admin"]}>
              <Supply />
            </ProtectedRoute>
          }
        />
        <Route
          path="fournisseurs"
          element={
            <ProtectedRoute requiredAccess={["reporting", "admin"]}>
              <Fournisseurs />
            </ProtectedRoute>
          }
        />
        <Route
          path="ressources"
          element={
            <ProtectedRoute requiredAccess={["resources", "admin"]}>
              <Resources />
            </ProtectedRoute>
          }
        />
        <Route
          path="catalogue"
          element={
            <ProtectedRoute requiredAccess={["catalog", "admin"]}>
              <Catalog />
            </ProtectedRoute>
          }
        />
        <Route
          path="distributeurs"
          element={
            <ProtectedRoute requiredAccess={["retailers", "admin"]}>
              <Distributeurs />
            </ProtectedRoute>
          }
        />
        <Route
          path="projets"
          element={
            <ProtectedRoute>
              <ProjectsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="harven"
          element={
            <ProtectedRoute>
              <HarvenChat />
            </ProtectedRoute>
          }
        />
        {/* Autres routes à ajouter ici */}
      </Route>
    </Routes>
  );
}

export default App;
